// The sequence builder's state (GRO-287): plain functions over one value, so the tab's
// component only renders and every rule here is tested without React.
//
// Amplemarket appends steps and cannot reorder or delete them through its MCP, so all of that
// happens here, before creation. Once a draft has an Amplemarket sequence it is locked.

import type {
  Contact, SequenceConnection, SequenceCreateOutcome, SequenceCreation, SequenceDraft, SequenceEmailMode, SequenceMailboxes,
  SequenceMissingField, SequenceStep, SequenceStepType,
} from '../api/types.ts';
import { DEFAULT_CAMPAIGN_TYPE, isCampaignType, splitName, unknownPrefix } from './campaignTypes.ts';
import { hasUsableName, isSharedInbox } from './contactName.ts';
import { foundNotCleared } from './foundByScout.ts';
import { playgroundNeedsName } from './playgroundContacts.ts';

export const STEP_TYPES: { type: SequenceStepType; label: string }[] = [
  { type: 'email', label: 'Email' },
  { type: 'phone_call', label: 'Call' },
  { type: 'linkedin_connection_request', label: 'LinkedIn connect' },
  { type: 'linkedin_message', label: 'LinkedIn message' },
  { type: 'linkedin_profile_visit', label: 'LinkedIn profile visit' },
  { type: 'linkedin_follow', label: 'LinkedIn follow' },
  { type: 'linkedin_like_last_post', label: 'LinkedIn like last post' },
  { type: 'linkedin_voice_message', label: 'LinkedIn voice message' },
  { type: 'linkedin_video_message', label: 'LinkedIn video message' },
];

// Kept in step with sequence_builder/steps.py.
const STEPS_WITH_COPY: ReadonlySet<SequenceStepType> = new Set<SequenceStepType>([
  'email', 'phone_call', 'linkedin_connection_request', 'linkedin_message',
  'linkedin_voice_message', 'linkedin_video_message',
]);
const DRAFTABLE_STEPS: ReadonlySet<SequenceStepType> = new Set<SequenceStepType>([
  'email', 'phone_call', 'linkedin_connection_request', 'linkedin_message',
]);
export const MAX_STEPS = 15;
export const MAX_CONTACTS = 25;
export const MAX_DELAY_DAYS = 60;

export interface BuilderState {
  draftId: string | null;
  // The name as typed, without the campaign-type prefix; the server puts "[campaignType] " in front.
  name: string;
  campaignType: string;
  contactIds: string[];
  steps: SequenceStep[];
  // Contact id → that person's {{personal_line}}.
  personalLines: Record<string, string>;
  locked: boolean;
  dirty: boolean;
}

export const PERSONAL_LINE_FIELD = 'personal_line';

// Scout writes at most this many personal lines per click: each one is a full draft.
export const MAX_PERSONAL_LINES_PER_FILL = 10;

export function stepLabel(type: SequenceStepType): string {
  return STEP_TYPES.find(option => option.type === type)?.label ?? type;
}

export function stepHasCopy(type: SequenceStepType): boolean {
  return STEPS_WITH_COPY.has(type);
}

export function stepIsDraftable(type: SequenceStepType): boolean {
  return DRAFTABLE_STEPS.has(type);
}

export function copyLabel(type: SequenceStepType): string {
  if (type === 'phone_call') return 'Talk track';
  if (type === 'linkedin_connection_request') return 'Connection note';
  if (type === 'linkedin_voice_message' || type === 'linkedin_video_message') return 'Script';
  return 'Message';
}

export function emptyBuilder(): BuilderState {
  return {
    draftId: null, name: '', campaignType: DEFAULT_CAMPAIGN_TYPE, contactIds: [], steps: [], personalLines: {},
    locked: false, dirty: false,
  };
}

// A saved name's prefix becomes the dropdown's value. A draft saved before the prefix has none
// and opens as the default type; a prefix off the list stays in the name for the strategist to fix.
export function builderFromDraft(draft: SequenceDraft): BuilderState {
  const split = splitName(draft.name);
  const known = isCampaignType(split.prefix);
  return {
    draftId: draft.draft_id,
    name: known ? split.rest : draft.name,
    campaignType: known ? split.prefix as string : DEFAULT_CAMPAIGN_TYPE,
    contactIds: [...draft.contact_ids],
    steps: draft.steps.map(step => ({ ...step })),
    personalLines: { ...(draft.personal_lines ?? {}) },
    locked: draft.amplemarket_sequence_id !== null || draft.status !== 'draft' || Boolean(draft.create_attempted_at),
    dirty: false,
  };
}

// A new step's id only has to be unique within the draft.
function nextStepId(steps: SequenceStep[]): string {
  let highest = 0;
  for (const step of steps) {
    const match = /^s(\d+)$/.exec(step.step_id);
    if (match) highest = Math.max(highest, Number(match[1]));
  }
  return `s${highest + 1}`;
}

// The first step goes out at once; later ones default to a few days after the last.
function defaultDelay(steps: SequenceStep[]): number {
  return steps.length === 0 ? 0 : 3;
}

export function addStep(state: BuilderState, type: SequenceStepType): BuilderState {
  if (state.locked || state.steps.length >= MAX_STEPS) return state;
  const step: SequenceStep = {
    step_id: nextStepId(state.steps), type, delay_days: defaultDelay(state.steps),
    email_mode: 'automatic', subject: '', body: '',
  };
  return { ...state, steps: [...state.steps, step], dirty: true };
}

export function removeStep(state: BuilderState, stepId: string): BuilderState {
  if (state.locked) return state;
  return { ...state, steps: state.steps.filter(step => step.step_id !== stepId), dirty: true };
}

export function moveStep(state: BuilderState, stepId: string, direction: -1 | 1): BuilderState {
  if (state.locked) return state;
  const index = state.steps.findIndex(step => step.step_id === stepId);
  const target = index + direction;
  if (index < 0 || target < 0 || target >= state.steps.length) return state;
  const steps = [...state.steps];
  const [moved] = steps.splice(index, 1);
  steps.splice(target, 0, moved!);
  return { ...state, steps, dirty: true };
}

export interface StepEdit {
  type?: SequenceStepType;
  delay_days?: number;
  email_mode?: SequenceEmailMode;
  subject?: string;
  body?: string;
}

export function clampDelay(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(MAX_DELAY_DAYS, Math.max(0, Math.round(value)));
}

export function updateStep(state: BuilderState, stepId: string, edit: StepEdit): BuilderState {
  if (state.locked) return state;
  const steps = state.steps.map(step => {
    if (step.step_id !== stepId) return step;
    const updated: SequenceStep = { ...step, ...edit };
    if (edit.delay_days !== undefined) updated.delay_days = clampDelay(edit.delay_days);
    // Changing type drops what the new type cannot carry.
    if (!stepHasCopy(updated.type)) { updated.subject = ''; updated.body = ''; }
    if (updated.type !== 'email') { updated.subject = ''; updated.email_mode = 'automatic'; }
    return updated;
  });
  return { ...state, steps, dirty: true };
}

export function toggleContact(state: BuilderState, contactId: string): BuilderState {
  if (state.locked) return state;
  if (state.contactIds.includes(contactId)) {
    return { ...state, contactIds: state.contactIds.filter(id => id !== contactId), dirty: true };
  }
  if (state.contactIds.length >= MAX_CONTACTS) return state;
  return { ...state, contactIds: [...state.contactIds, contactId], dirty: true };
}

// A new builder holding the seeded people Create could send to, best first (Outreach's "New
// sequence" passes its selection). Marked dirty, so the newest saved draft does not replace it.
export function seededBuilder(contacts: Contact[], seedContactIds: string[]): BuilderState {
  return withSeededContacts(emptyBuilder(), contacts, seedContactIds);
}

// People ticked in Outreach since the builder opened, added to it (never removed), best first.
// A locked builder (a sequence already in Amplemarket) is left alone: a fresh one holding the
// ticked people opens only through "New sequence".
export function withSeededContacts(state: BuilderState, contacts: Contact[], seedContactIds: string[]): BuilderState {
  if (state.locked) return state;
  const newIds = rankedContacts(contacts)
    .filter(contact => seedContactIds.includes(contact.id) && contactBlocker(contact) === null)
    .map(contact => contact.id)
    .filter(contactId => !state.contactIds.includes(contactId));
  if (!newIds.length) return state;
  return newIds.reduce((current, contactId) => toggleContact(current, contactId), state);
}

// Whether Scout has a name to write to for this person: a real or accepted name, or one read
// off a first.last address on the strict rule Create uses (`voice.ai@` is not "Voice"). The
// server asks the same (contact_names.py, strict).
export function contactHasName(contact: Contact): boolean {
  return hasUsableName(contact, true);
}

// What the builder says, once, beside a picked person with no name at all. They can still be
// sequenced; Scout skips them when it writes, and Create asks for any field the copy needs.
export function contactNameHint(contact: Contact): string | null {
  if (contactHasName(contact) || contactBlocker(contact) !== null) return null;
  return 'no name yet: use Find name in Outreach';
}

// A known "[Type] " typed at the start moves into the dropdown, so the name is never double-prefixed.
export function renameBuilder(state: BuilderState, name: string): BuilderState {
  if (state.locked) return state;
  const split = splitName(name);
  if (isCampaignType(split.prefix)) return { ...state, name: split.rest, campaignType: split.prefix, dirty: true };
  return { ...state, name, dirty: true };
}

export function setCampaignType(state: BuilderState, campaignType: string): BuilderState {
  if (state.locked || !isCampaignType(campaignType)) return state;
  return { ...state, campaignType, dirty: true };
}

// Heroes first (W3), then everyone else in book order.
export function rankedContacts(contacts: Contact[]): Contact[] {
  return contacts
    .map((contact, index) => ({ contact, index }))
    .sort((left, right) => {
      const leftRank = left.contact.hero_rank ?? Number.POSITIVE_INFINITY;
      const rightRank = right.contact.hero_rank ?? Number.POSITIVE_INFINITY;
      if (leftRank !== rightRank) return leftRank - rightRank;
      return left.index - right.index;
    })
    .map(entry => entry.contact);
}

// The builder's contact list: ranked, without shared inboxes (sales@, support@) unless a saved
// draft or the Outreach seed already picked one, so it shows up to be removed, not sent silently.
export function builderContacts(contacts: Contact[], pickedContactIds: string[]): Contact[] {
  return rankedContacts(contacts)
    .filter(contact => !isSharedInbox(contact.email) || pickedContactIds.includes(contact.id));
}

// Why this person cannot go into a sequence, or null. The server checks again at create time.
export function contactBlocker(contact: Contact): string | null {
  if (contact.opted_out) return 'opted out';
  if (contact.bounced) return 'bounced';
  if (contact.amplemarket_excluded) return 'excluded in Amplemarket';
  if (foundNotCleared(contact)) return 'not cleared to send';
  if (!contact.email && !contact.linkedin) return 'no email or LinkedIn';
  if (playgroundNeedsName(contact)) return 'no name yet: use Find name';
  return null;
}

// How "Create in Amplemarket" behaves here: for real, as a dry run (a preview), or as a real
// draft on a preview with the opt-in. Only `dry_run` may create without a connection.
export type CreateMode = 'live' | 'dry_run' | 'preview_real_draft';

export function createMode(connection: SequenceConnection | null): CreateMode {
  if (!connection?.preview) return 'live';
  return connection.preview_creates_real_draft ? 'preview_real_draft' : 'dry_run';
}

export function previewCreateNote(mode: CreateMode): string | null {
  if (mode === 'dry_run') return 'Create shows what would be created. Nothing is sent.';
  if (mode === 'preview_real_draft') return 'Preview: creates a real draft in Amplemarket';
  return null;
}

// A create went out and nobody knows whether it landed: the strategist checks Amplemarket.
export function createOutcomeUnknown(draft: SequenceDraft | null): boolean {
  return Boolean(draft && draft.create_attempted_at && !draft.amplemarket_sequence_id);
}

// What still stands between this draft and "Create in Amplemarket". `missingFields` is the
// server's check as of the last save; it only counts while there are no unsaved edits.
export function createBlockers(state: BuilderState, missingFields: SequenceMissingField[] = []): string[] {
  const blockers: string[] = [];
  if (state.steps.length === 0) blockers.push('Add a step.');
  if (state.contactIds.length === 0) blockers.push('Pick at least one contact.');
  state.steps.forEach((step, index) => {
    if (step.type === 'email' && !step.subject.trim() && index === firstEmailIndex(state.steps)) {
      blockers.push(`Step ${index + 1} needs a subject.`);
    }
    if (stepHasCopy(step.type) && step.type !== 'phone_call' && !step.body.trim()) {
      blockers.push(`Step ${index + 1} has no copy.`);
    }
  });
  if (personalLinesWithLiquid(state).length) blockers.push('A personal line cannot contain {{ or {%.');
  const typedPrefix = unknownPrefix(state.name);
  if (typedPrefix !== null) blockers.push(`[${typedPrefix}] is not a campaign type. Pick one from the list.`);
  if (state.dirty) blockers.push('Saving the draft…');
  else if (missingFields.length) blockers.push('Fill the missing fields first.');
  return blockers;
}

function firstEmailIndex(steps: SequenceStep[]): number {
  return steps.findIndex(step => step.type === 'email');
}

// The day each step goes out, counting from enrolment: delays add up along the sequence.
export function stepDays(steps: SequenceStep[]): number[] {
  let day = 0;
  return steps.map(step => { day += step.delay_days; return day; });
}

export function savePayload(state: BuilderState, accountId: string) {
  return {
    draft_id: state.draftId,
    account_id: accountId,
    name: state.name.trim(),
    campaign_type: state.campaignType,
    contact_ids: state.contactIds,
    steps: state.steps,
    personal_lines: state.personalLines,
  };
}

// --- writing for everyone: personal lines and Liquid fields --------------------------------

export function setPersonalLine(state: BuilderState, contactId: string, text: string): BuilderState {
  if (state.locked) return state;
  return { ...state, personalLines: { ...state.personalLines, [contactId]: text }, dirty: true };
}

// Every `{{ field }}` (with or without filters) for this one field name.
function fieldPattern(field: string): RegExp {
  const escaped = field.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`\\{\\{\\s*${escaped}\\s*(\\|[^}]*)?\\}\\}`, 'g');
}

function withoutField(text: string, field: string): string {
  return text
    .replace(fieldPattern(field), '')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/^[ \t]+|[ \t]+$/gm, '');
}

// "Remove the field": take it out of every step's subject and copy.
export function removeField(state: BuilderState, field: string): BuilderState {
  if (state.locked) return state;
  const steps = state.steps.map(step => ({
    ...step, subject: withoutField(step.subject, field), body: withoutField(step.body, field),
  }));
  return { ...state, steps, dirty: true };
}

export function copyUsesField(steps: SequenceStep[], field: string): boolean {
  return steps.some(step => fieldPattern(field).test(step.subject) || fieldPattern(field).test(step.body));
}

// The server's missing-field list, grouped for the page: one row per field, with who lacks it.
export interface MissingFieldGroup { field: string; contactIds: string[]; names: string[] }

export function missingFieldGroups(missing: SequenceMissingField[]): MissingFieldGroup[] {
  const groups: MissingFieldGroup[] = [];
  for (const item of missing) {
    let group = groups.find(existing => existing.field === item.field);
    if (!group) {
      group = { field: item.field, contactIds: [], names: [] };
      groups.push(group);
    }
    group.contactIds.push(item.contact_id);
    group.names.push(item.name);
  }
  return groups;
}

// --- the sending mailbox ---------------------------------------------------------------------

// The mailboxes the strategist may pick from: usable ones only. A dropdown shows when there are two or more.
export function usableMailboxes(mailboxes: SequenceMailboxes | null): string[] {
  return (mailboxes?.mailboxes ?? []).filter(mailbox => mailbox.usable).map(mailbox => mailbox.email);
}

// --- what a create did, in plain words --------------------------------------------------------

export interface CreationHeadline { tone: 'added' | 'warn' | 'bad'; text: string }

// Never "partly created" when nothing exists in Amplemarket: whether a sequence was made decides
// the sentence, then how many steps and people made it in.
export function creationHeadline(creation: SequenceCreation, outcome: SequenceCreateOutcome | null,
                                 sequenceExists: boolean): CreationHeadline {
  if (outcome?.status === 'not_created' || !sequenceExists) {
    return { tone: 'bad', text: 'Nothing was created in Amplemarket.' };
  }
  const stepsAdded = creation.steps_added ?? 0;
  const stepsTotal = creation.steps_total ?? 0;
  if (stepsAdded < stepsTotal) {
    return {
      tone: 'warn',
      text: `The draft sequence is in Amplemarket, but only ${stepsAdded} of ${stepsTotal} steps were added.`,
    };
  }
  const addedCount = (creation.leads_added ?? []).length;
  const failedCount = (creation.lead_failures ?? []).length;
  const steps = `${stepsTotal} ${stepsTotal === 1 ? 'step' : 'steps'}`;
  if (failedCount > 0) {
    return {
      tone: 'warn',
      text: `Created in Amplemarket as a draft with ${steps}. ${addedCount} of ${addedCount + failedCount} people were added.`,
    };
  }
  const people = `${addedCount} ${addedCount === 1 ? 'person' : 'people'}`;
  return { tone: 'added', text: `Created in Amplemarket as a draft: ${steps}, ${people}.` };
}

// Personal lines are plain text: Liquid in one would run per lead. The server refuses them too.
export function personalLinesWithLiquid(state: BuilderState): string[] {
  return Object.entries(state.personalLines)
    .filter(([, line]) => line.includes('{{') || line.includes('{%'))
    .map(([contactId]) => contactId);
}

// A field read inside a `{% %}` tag: "Remove the field" cannot take it out without breaking the tag.
export function fieldInTag(steps: SequenceStep[], field: string): boolean {
  const escaped = field.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const pattern = new RegExp(`\\{%[^%]*\\b${escaped}\\b[^%]*%\\}`);
  return steps.some(step => pattern.test(step.subject) || pattern.test(step.body));
}

// The people a "Fill with Scout" click writes for, capped per click.
export function personalLinesToFill(contactIds: string[]): string[] {
  return contactIds.slice(0, MAX_PERSONAL_LINES_PER_FILL);
}

// After "Draft with Scout" writes the first email: everyone else picked who still has no
// personal line, so one click fills the line for every person, not just the drafted-against one.
export function personalLinesStillEmpty(state: BuilderState, contactIds: string[]): string[] {
  return contactIds.filter(contactId => !state.personalLines[contactId]?.trim());
}

// After a save of `savedFrom` comes back: take the server's draft only if nothing was edited
// while the save was out; otherwise keep the newer edits (still unsaved) under the saved id.
export function builderAfterSave(current: BuilderState, savedFrom: BuilderState, draft: SequenceDraft): BuilderState {
  if (current === savedFrom) return builderFromDraft(draft);
  return { ...current, draftId: draft.draft_id, dirty: true };
}

// The lead count a launch confirms: who the create actually added, which leaves out anyone
// withheld (exclusion list, a missing field). A preview counts who its create would add.
export function launchLeadCount(created: SequenceCreateOutcome): number {
  if (created.status === 'dry_run') return created.planned.filter(call => call.stage === 'add_leads').length;
  return (created.leads_added ?? []).length;
}

// The confirm dialog after the create of "Create and launch": names the sequence, how many people
// it will email, and who the create left out. Cancel keeps it a draft in Amplemarket.
export function launchConfirmText(sequenceName: string, leadCount: number, withheldNames: string[] = []): string {
  const people = `${leadCount} ${leadCount === 1 ? 'person' : 'people'}`;
  const leftOut = withheldNames.length ? `Left out: ${withheldNames.join(', ')}.\n\n` : '';
  return `“${sequenceName}” is created in Amplemarket. Launch it now for ${people}?\n\n${leftOut}`
    + 'Launching starts sending on the sequence\'s schedule. Cancel keeps it a draft in Amplemarket to review there.';
}

// One line for how a launch ended, from the reply or from the record on the draft.
export function launchHeadline(launch: { status: string; error?: string | null; launched_by?: string | null;
                                         lead_count?: number; planned?: string | null }): CreationHeadline {
  if (launch.status === 'launched') {
    const people = `${launch.lead_count ?? 0} ${launch.lead_count === 1 ? 'person' : 'people'}`;
    return { tone: 'added', text: `Launched in Amplemarket for ${people} by ${launch.launched_by ?? 'you'}.` };
  }
  if (launch.status === 'dry_run') {
    return { tone: 'warn', text: `Preview: nothing was launched. A launch would: ${launch.planned ?? 'launch it'}` };
  }
  if (launch.status === 'launching' || launch.status === 'unknown') {
    return { tone: 'warn', text: launch.error || 'The launch may have reached Amplemarket. Check it there.' };
  }
  return { tone: 'bad', text: launch.error || 'Not launched. The sequence is saved as a draft in Amplemarket; launch it from there.' };
}
